import * as React from 'react'

import { cn } from './cn'

export interface AppBarProps extends React.HTMLAttributes<HTMLElement> {
  position?: 'fixed' | 'absolute' | 'sticky' | 'static' | 'relative'
  color?: 'primary' | 'default' | 'transparent' | 'inherit'
  enableColorOnDark?: boolean
}

const positionClasses = {
  fixed: 'fixed top-0 left-auto right-0 w-full',
  absolute: 'absolute top-0 left-auto right-0 w-full',
  sticky: 'sticky top-0 left-auto right-0 w-full',
  static: 'static',
  relative: 'relative',
}

const colorClasses = {
  primary:
    'bg-gradient-to-r from-primary to-primary-dark text-primary-contrast',
  default:
    'bg-paper/80 text-text-primary backdrop-blur-md border-b border-divider',
  transparent: 'bg-transparent text-inherit shadow-none',
  inherit: 'bg-inherit text-inherit',
}

const AppBar = React.forwardRef<HTMLElement, AppBarProps>(
  (
    {
      position = 'fixed',
      color = 'primary',
      enableColorOnDark,
      className,
      ...props
    },
    ref,
  ) => (
    <header
      ref={ref}
      className={cn(
        'z-[1100] flex flex-col',
        positionClasses[position],
        colorClasses[color],
        // in dark mode, match HeadingContainer's paper background
        color === 'primary' &&
          !enableColorOnDark &&
          'dark:bg-paper dark:bg-none dark:text-text-primary',
        className,
      )}
      {...props}
    />
  ),
)
AppBar.displayName = 'AppBar'

export default AppBar

export interface ToolbarProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'regular' | 'dense'
  disableGutters?: boolean
  component?: React.ElementType
}

export const Toolbar = React.forwardRef<HTMLDivElement, ToolbarProps>(
  (
    {
      variant = 'regular',
      disableGutters,
      component: Component = 'div',
      className,
      ...props
    },
    ref,
  ) => (
    <Component
      ref={ref}
      className={cn(
        'relative flex items-center',
        variant === 'dense' ? 'min-h-[48px]' : 'min-h-[56px] sm:min-h-[64px]',
        !disableGutters && 'px-4 sm:px-6',
        className,
      )}
      {...props}
    />
  ),
)
Toolbar.displayName = 'Toolbar'
